import { Styleguide } from './s3';
import { sanitizeCssNumber, sanitizeFontFamily } from './sanitize/css';

interface FontWeight {
  fontFamily: string;
  fontWeight: number;
}

/**
 * Extrai as combinações únicas de fontFamily + fontWeight do styleguide
 */
export function extractFontWeights(styleguide: Styleguide): FontWeight[] {
  const { typographies } = styleguide;
  const seen = new Set<string>();
  const result: FontWeight[] = [];

  const addFont = (fontFamily: string, fontWeight: number) => {
    const key = `${fontFamily.toLowerCase()}-${fontWeight}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ fontFamily: fontFamily.toLowerCase(), fontWeight });
    }
  };

  addFont(typographies.HEADING.fontFamily, typographies.HEADING.fontWeight);
  addFont(typographies.SUB_HEADING.fontFamily, typographies.SUB_HEADING.fontWeight);
  addFont(typographies.TITLE.fontFamily, typographies.TITLE.fontWeight);
  addFont(typographies.TEXT.fontFamily, typographies.TEXT.fontWeight);

  return result;
}

/**
 * Gera as declarações @font-face para as fontes do styleguide
 */
export function generateFontFaceDeclarations(
  fontWeights: FontWeight[],
  baseUrl: string
): string {
  return fontWeights
    .map(({ fontFamily, fontWeight }) => {
      const family = sanitizeFontFamily(fontFamily);
      const weight = sanitizeCssNumber(fontWeight);
      const fontPath = `${baseUrl}/${family}/${weight}.woff2`;
      return `@font-face {
  font-family: '${family}';
  src: url('${fontPath}') format('woff2');
  font-weight: ${weight};
  font-style: normal;
  font-display: swap;
}`;
    })
    .join('\n\n');
}

/**
 * Gera o CSS completo de @font-face a partir do styleguide
 */
export function getFontFaceCssFromStyleguide(
  styleguide: Styleguide | null,
  baseUrl: string | undefined
): string | null {
  if (!styleguide || !baseUrl) return null;
  const fontWeights = extractFontWeights(styleguide);
  return fontWeights.length > 0
    ? generateFontFaceDeclarations(fontWeights, baseUrl)
    : null;
}

/**
 * CSS de `@font-face` do styleguide, já resolvendo a env do CDN de fontes.
 * Compartilhado pelos dois roteadores para a geração não divergir entre eles.
 */
export function resolveFontFaceCss(styleguide: Styleguide | null): string | null {
  return getFontFaceCssFromStyleguide(
    styleguide,
    process.env.NEXT_PUBLIC_STATIC_FONTS_BASE_URL,
  );
}

/**
 * Origem do CDN de fontes, para o `preconnect`. `null` quando a env não está configurada
 * ou não é uma URL válida.
 */
export function getFontsPreconnectOrigin(): string | null {
  const baseUrl = process.env.NEXT_PUBLIC_STATIC_FONTS_BASE_URL;

  try {
    return baseUrl ? new URL(baseUrl).origin : null;
  } catch {
    return null;
  }
}
